import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { JournalList } from '@/components/journal/journal-list';
import { JOURNAL_SORTS, type JournalSort } from '@/lib/journal-search';
import { useReturnTicket } from '@/lib/return-ticket';
import { parseSearchEnum, parseSearchString } from '@/lib/search-params';

/**
 * What `/journals` reads out of its own URL.
 *
 * A search box and a sort, and deliberately the same vocabulary as `/recipes`,
 * `/batches` and `/blending` (PLAN-LIST-STATE D-LS10): `?q=` means the search
 * box everywhere in the app, so a brewer who has learned one address bar has
 * learned all four.
 */
export interface JournalSearch {
  /** The search box. */
  readonly q: string | undefined;
  /** The sort control, or `undefined` for the default (`updated`). */
  readonly sort: JournalSort | undefined;
}

export const Route = createFileRoute('/journals/')({
  validateSearch: (
    search: { readonly q?: unknown; readonly sort?: unknown } & SearchSchemaInput,
  ): JournalSearch => ({
    q: parseSearchString(search.q),
    sort: parseSearchEnum(search.sort, JOURNAL_SORTS),
  }),
  component: JournalIndexPage,
});

function JournalIndexPage() {
  const { q, sort } = Route.useSearch();
  // Scoped `from`, not the bare hook: an unscoped `useNavigate` types `search`
  // against every route's schema at once, which cannot know this route's own
  // `q`/`sort` shape and rejects the updaters below.
  const navigate = useNavigate({ from: Route.fullPath });
  // The view is in the URL; this is what lets the journal page come back to it
  // (PLAN-LIST-STATE D-LS7).
  const returnTicket = useReturnTicket();
  return (
    <JournalList
      // The search *is* the view: `JournalList` reads these on every render
      // rather than seeding from them, so a Back that only changes the search
      // params — which does not remount this route — moves the list with it.
      query={q}
      sort={sort}
      // `replace: true`: a keystroke narrows the same search, it does not start
      // a new one, so Back should undo the whole search box rather than walk
      // back through "m", "me", "mea" one letter at a time.
      onQueryChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, q: next === '' ? undefined : next }),
          replace: true,
        });
      }}
      // `updated` is the list's default and never reaches the URL (D-LS2): one
      // view spelled two ways is a view Back can land on twice.
      onSortChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, sort: next === 'updated' ? undefined : next }),
        });
      }}
      // Clearing is a decision, not a keystroke: it pushes, so Back puts the
      // search the brewer had just cleared back on the screen.
      onClearFilters={() => {
        void navigate({ search: (prev) => ({ ...prev, q: undefined }) });
      }}
      onOpen={(journalId) => {
        void navigate({ to: '/journals/$journalId', params: { journalId }, state: returnTicket });
      }}
    />
  );
}
